import type { MemberDetail } from '@/data/api'
import type { Account } from '@/data/types'
import type { AgentNode } from '@/api/map/agent'
import { enrichTreeAccounts } from '@/api/map/downline'

/** 7 rows (Mon–Sun) × 24 hours of zeros — the shape the heatmap expects. */
function emptyActivity(): number[][] {
  return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0))
}

/**
 * MemberDetail from the hierarchy tree.
 *
 * Identity, balance, credit limit, share, member counts and the upline chain are
 * real (hierarchy-service). Turnover / P&L / bets / stake analytics have no live
 * source yet (bet-service + analytics marts are not deployed — CLAUDE.md
 * directive 13), so those fields are zeroed and the page shows its empty states.
 */
export function buildMemberDetail(
  nodes: AgentNode[],
  memberId: string,
  _selfId: string,
): MemberDetail | null {
  const accounts = enrichTreeAccounts(nodes)
  const byId = new Map(accounts.map((a) => [a.id, a]))
  const account = byId.get(memberId)
  if (!account) return null

  const upline: Account[] = []
  const seen = new Set<string>()
  let cursor = account.parentId
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    const parent = byId.get(cursor)
    if (!parent) break
    upline.unshift(parent)
    cursor = parent.parentId
  }

  return {
    account,
    upline,
    daily: [],
    bySport: [],
    activity: emptyActivity(),
    stakeBuckets: [],
    lifetime: {
      turnover: 0,
      memberPnl: 0,
      commission: 0,
      bets: 0,
      settledBets: 0,
      wins: 0,
      avgStake: 0,
      biggestWin: 0,
      biggestLoss: 0,
    },
    openExposure: account.exposure,
  }
}

/** Enriched account (with child/member counts) for one id, from the tree. */
export function findEnrichedAccount(nodes: AgentNode[], id: string): Account | null {
  return enrichTreeAccounts(nodes).find((a) => a.id === id) ?? null
}

/**
 * Overlay a single-node read onto the tree list.
 *
 * `GET /agents/{id}/tree` omits `balance`; only `GET /agents/{id}` returns it.
 * The tree still supplies adjacency (counts, upline), so the node read is merged
 * in rather than replacing it.
 */
export function overlayNode(nodes: AgentNode[], node: AgentNode): AgentNode[] {
  if (!nodes.some((n) => n.id === node.id)) return [...nodes, node]
  return nodes.map((n) => (n.id === node.id ? { ...n, ...node } : n))
}
